import { fireAndForget } from './fireAndForget';
import { isNestedError } from '@paid-search/public-api/src/accountHotel/NestedError';
import { JSONableErrorWrapper } from '@paid-search/core/src/error';

export type RollbarConfig = {
  accessToken: string;
  payload: {
    environment: string;
    scriptVersion: string;
    client: {
      javascript: {
        source_map_enabled: boolean;
        guess_uncaught_frames: boolean;
      };
    };
  };
};

export type RollbarLevel = 'critical' | 'error' | 'warning' | 'info' | 'debug';

export interface Rollbar {
  error(error: Error): void;

  warn(error: Error): void;

  info(message: string, additionalInformation?: {}): void;
}

export default class MiniRollbar implements Rollbar {
  constructor(private config: RollbarConfig, private window: Window, private fire = fireAndForget) {}

  error(error: Error) {
    this.sendTraceChain('error', error);
  }

  warn(error: Error) {
    this.sendTraceChain('warning', error);
  }

  info(message: string, additionalInformation?: Object) {
    this.sendMessage('info', message, additionalInformation);
  }

  private sendMessage(level: RollbarLevel, message: string, additionalInformation?: Object) {
    this.sendToRollbar(
      level,
      {
        message: {
          body: message,
        },
      },
      additionalInformation,
    );
  }

  private sendTraceChain(level: RollbarLevel, error: Error) {
    const body = this.toTraceChain(error);
    const frames = body.trace_chain[0].frames;

    if (frames.filter((frame: { filename: string }) => frame.filename.indexOf('triptease.io') !== -1).length === 0) {
      return;
    }
    this.sendToRollbar(level, body, JSONableErrorWrapper.from(error).toJSON());
  }

  private toTraceChain(error: Error) {
    let traceChain: Array<Error> = [error];

    if (isNestedError(error)) {
      traceChain = [error.innerError, ...traceChain];
    }

    return {
      trace_chain: traceChain.map((e) => this.getTrace(e)),
    };
  }

  private sendToRollbar(level: RollbarLevel, body: Object, custom?: Object) {
    this.fire({
      url: 'https://api.rollbar.com/api/1/item/',
      body: {
        access_token: this.config.accessToken,
        data: {
          ...this.config.payload,
          client: {
            ...this.config.payload.client,
            javascript: {
              ...this.config.payload.client.javascript,
              browser: this.window.navigator.userAgent,
              code_version: this.config.payload.scriptVersion,
            },
          },
          level,
          body,
          request: {
            url: this.window.location.href,
          },
          platform: 'browser',
          language: 'javascript',
          custom,
        },
      },
    });
  }

  private getTrace(error: Error) {
    return {
      frames: this.frames(error),
      exception: {
        class: error.name,
        message: error.message,
      },
    };
  }

  private frames(error: Error) {
    if (!error.stack) return [];
    const pattern = /at (?:([^(]+)? \(?)?(.+):(\d+):(\d+)\)?/g;
    const result = [];
    while (true) {
      const match = pattern.exec(error.stack);
      if (!match) return result;
      const [, method, filename, lineno, colno] = match;
      result.unshift({
        filename,
        lineno: parseInt(lineno),
        colno: parseInt(colno),
        method,
      });
    }
  }
}
